import { Injectable } from '@angular/core';

import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
  tap,
} from 'rxjs';

import { BffUserService } from './bff.user.service';

/**
 * Client-side cache of the current user's permissions.
 *
 * Permissions are fetched once (via {@link BffUserService}, which calls `auth/getuser`)
 * and held in a `Set` for O(1) synchronous lookups. Prefer the synchronous helpers
 * (`has`, `hasAny`, `hasAll`) inside already-rendered code, and the `*$` observable
 * variants where the check may run before permissions have loaded (guards, directives).
 *
 * This runs alongside the existing role checks on {@link BffUserService} - callers can
 * migrate from role assertions to permission assertions one at a time.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private grantedPermissions = new Set<string>();
  private readonly loadedSubject = new BehaviorSubject<boolean>(false);

  /** Emits `true` once permissions have been loaded from the backend. */
  readonly loaded$ = this.loadedSubject.asObservable();

  /** The user's permissions, kept warm so late subscribers get the cached value. */
  readonly permissions$: Observable<string[]>;

  constructor(private readonly bffUserService: BffUserService) {
    this.permissions$ = this.bffUserService.permissions$.pipe(
      tap((permissions) => {
        this.grantedPermissions = new Set(permissions);
        this.loadedSubject.next(true);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  /**
   * Triggers the one-off permission fetch. Safe to call multiple times - the
   * underlying user request is cached. Call from an app initializer so permissions
   * are ready before the first route resolves.
   */
  loadPermissions(): Observable<string[]> {
    return this.permissions$;
  }

  /** Synchronous check. Returns `false` until permissions have loaded. */
  has(permission: string): boolean {
    return this.grantedPermissions.has(permission);
  }

  hasAll(permissions: string[]): boolean {
    return permissions.every((p) => this.grantedPermissions.has(p));
  }

  hasAny(permissions: string[]): boolean {
    return permissions.some((p) => this.grantedPermissions.has(p));
  }

  /** Snapshot of the currently granted permissions. */
  get permissions(): string[] {
    return [...this.grantedPermissions];
  }

  has$(permission: string): Observable<boolean> {
    return this.hasAll$([permission]);
  }

  hasAll$(permissions: string[]): Observable<boolean> {
    return this.permissions$.pipe(
      map((granted) => permissions.every((p) => granted.includes(p))),
      distinctUntilChanged()
    );
  }

  hasAny$(permissions: string[]): Observable<boolean> {
    return this.permissions$.pipe(
      map((granted) => permissions.some((p) => granted.includes(p))),
      distinctUntilChanged()
    );
  }
}
